import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, Trash2, X } from "lucide-react";

// Same field look as the lesson plan creator: thick gray border, rounded-xl, blue focus ring.
export const inputCls =
  "w-full rounded-xl border-2 border-gray-200 bg-white px-3.5 py-2.5 text-[15px] text-gray-900 font-[poppins] placeholder:text-gray-400 " +
  "transition-all duration-200 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 " +
  "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500";

const wrapCls =
  "flex flex-wrap items-center gap-1.5 rounded-xl border-2 border-gray-200 bg-white p-2 transition-all duration-200 " +
  "focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100";

export function FieldLabel({ children }) {
  return <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{children}</div>;
}

function Chip({ children, onRemove, disabled, tone = "blue" }) {
  const tones = {
    blue: "border-[#d7e6fa] bg-[#eef5ff] text-[#1565c0]",
    slate: "border-slate-200 bg-slate-100 text-slate-700",
  };
  return (
    <span className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-[12px] ${tones[tone]}`}>
      <span className="truncate">{children}</span>
      {!disabled && (
        <button type="button" onClick={onRemove} aria-label={`Remove ${children}`} className="shrink-0 opacity-60 hover:opacity-100">
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/** Free-text chips: type and press Enter (or comma) to add. */
export function TagInput({ values, onChange, disabled, placeholder = "Type and press Enter" }) {
  const [text, setText] = useState("");
  const add = () => {
    const v = text.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setText("");
  };
  return (
    <div className={wrapCls}>
      {values.map((v, i) => (
        <Chip key={`${v}-${i}`} tone="slate" disabled={disabled} onRemove={() => onChange(values.filter((_, j) => j !== i))}>
          {v}
        </Chip>
      ))}
      {!disabled && (
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            } else if (e.key === "Backspace" && !text && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={add}
          placeholder={values.length ? "" : placeholder}
          className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-[13px] outline-none placeholder:text-slate-400"
        />
      )}
    </div>
  );
}

/**
 * Pick from a list OR type a custom value. `selected` are values from
 * `options`; `custom` are free-text additions, stored separately (the backend
 * keeps them in competencies/customCompetencies and pedagogies/customPedagogies).
 */
export function MultiPick({ options, selected, custom, onChange, disabled, placeholder, addLabel = "Add custom" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => options.filter((o) => !q || o.toLowerCase().includes(q)), [options, q]);
  const exact = options.some((o) => o.toLowerCase() === q) || custom.some((c) => c.toLowerCase() === q);

  const toggle = (opt) =>
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt], custom);
  const addCustom = () => {
    const v = query.trim();
    if (!v || exact) return;
    onChange(selected, [...custom, v]);
    setQuery("");
  };

  return (
    <div ref={ref} className="relative">
      <div
        className={`flex min-h-[46px] flex-wrap items-center gap-1.5 rounded-xl border-2 bg-white p-2 transition-all duration-200 ${
          open ? "border-blue-500 ring-4 ring-blue-100" : "border-gray-200"
        } ${disabled ? "bg-gray-50" : "cursor-text"}`}
        onClick={() => !disabled && setOpen(true)}
      >
        {selected.map((s) => (
          <Chip key={`o-${s}`} disabled={disabled} onRemove={() => toggle(s)}>{s}</Chip>
        ))}
        {custom.map((c, i) => (
          <Chip key={`c-${c}-${i}`} tone="slate" disabled={disabled} onRemove={() => onChange(selected, custom.filter((_, j) => j !== i))}>
            {c}
          </Chip>
        ))}
        {!disabled && (
          <>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filtered.length === 1 && q) toggle(filtered[0]);
                  else addCustom();
                } else if (e.key === "Escape") setOpen(false);
              }}
              placeholder={selected.length + custom.length ? "" : placeholder}
              className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-[13px] outline-none placeholder:text-slate-400"
            />
            <ChevronDown className="mr-1 h-4 w-4 shrink-0 text-slate-400" />
          </>
        )}
      </div>
      {open && !disabled && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
          {filtered.length === 0 && !q && <div className="px-3 py-2 text-[12px] text-slate-500">No options available. Type to add your own.</div>}
          {filtered.map((opt) => {
            const on = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] hover:bg-[#f4f8fe] ${on ? "text-[#1565c0]" : "text-slate-700"}`}
              >
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? "border-[#1565c0] bg-[#1565c0]" : "border-slate-300"}`}>
                  {on && <Check className="h-3 w-3 text-white" />}
                </span>
                <span>{opt}</span>
              </button>
            );
          })}
          {q && !exact && (
            <button
              type="button"
              onClick={addCustom}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] font-medium text-[#1565c0] hover:bg-[#f4f8fe]"
            >
              <Plus className="h-4 w-4" /> {addLabel}: “{query.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** One-line-per-item list (learning objectives). Enter adds the next line. */
export function LineList({ values, onChange, disabled, placeholder, addLabel }) {
  const refs = useRef([]);
  const update = (i, v) => onChange(values.map((x, j) => (j === i ? v : x)));
  const remove = (i) => onChange(values.filter((_, j) => j !== i));
  const add = () => {
    onChange([...values, ""]);
    setTimeout(() => refs.current[values.length]?.focus(), 0);
  };
  return (
    <div className="space-y-1.5">
      {values.map((v, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="w-5 shrink-0 text-right text-[11px] text-slate-400">{i + 1}.</span>
          <input
            ref={(el) => (refs.current[i] = el)}
            value={v}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => update(i, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !disabled) {
                e.preventDefault();
                add();
              }
            }}
            className={inputCls}
          />
          {!disabled && (
            <button type="button" onClick={() => remove(i)} aria-label="Remove" className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button type="button" onClick={add} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1565c0] hover:underline">
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </button>
      )}
    </div>
  );
}

/** Two-step delete so a stray click never removes a unit full of chapters. */
export function ConfirmDelete({ label, onConfirm, disabled }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 3500);
    return () => clearTimeout(t);
  }, [armed]);
  if (disabled) return null;
  return armed ? (
    <button type="button" onClick={onConfirm} className="rounded-lg bg-rose-600 px-2.5 py-1.5 text-[12px] font-semibold text-white hover:bg-rose-700">
      Confirm delete
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setArmed(true)}
      aria-label={label}
      title={label}
      className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );
}
